import Vue from 'vue';
import Router from 'vue-router';
import { db, auth } from '@/plugins/firebase';
import Login from './views/Login.vue';
import LoginRedirect from './views/LogInRedirect.vue';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/login',
      name: 'login',
      component: Login,
    },
    {
      path: '/redirect',
      name: 'redirect',
      component: LoginRedirect,
      meta: { requiresAuth: true },
    },
    {
      path: '/admin',
      component: () => import(/* webpackChunkName: "admin" */ './views/AdminDashboard.vue'),
      meta: { requiresAuth: true, accountType: 'admin' },
      children: [
        {
          path: 'properties',
          component: () => import(/* webpackChunkName: "admin" */ './components/Properties.vue'),
        },
        {
          path: 'tenants',
          component: () => import(/* webpackChunkName: "admin" */ './components/Tenants.vue'),
        },
        {
          path: 'maintenance',
          component: () => import(/* webpackChunkName: "admin" */ './components/Maintenance.vue'),
        },
        {
          path: 'accounting',
          component: () => import(/* webpackChunkName: "admin" */ './components/Accounting.vue'),
          name: 'admin',
        },
        {
          path: 'add-property',
          component: () => import(/* webpackChunkName: "admin" */ './components/AddProperty.vue'),
        },
        {
          path: 'add-tenant',
          component: () => import(/* webpackChunkName: "admin" */ './components/AddTenant.vue'),
        },
        {
          path: 'add-vendor',
          component: () => import(/* webpackChunkName: "admin" */ './components/AddVendor.vue'),
        },
        {
          path: 'edit-property/:id',
          component: () => import(/* webpackChunkName: "admin" */ './components/EditProperty.vue'),
        },
        {
          path: 'edit-tenant/:id',
          component: () => import(/* webpackChunkName: "admin" */ './components/EditTenant.vue'),
        },
        {
          path: 'add-owner',
          component: () => import(/* webpackChunkName: "admin" */ './components/AddOwner.vue'),
        },
        {
          path: 'owners',
          component: () => import(/* webpackChunkName: "admin" */ './components/Owners.vue'),
        },
        {
          path: 'account',
          component: () => import(/* webpackChunkName: "admin" */ './components/AdminDetails.vue'),
        },
        {
          path: 'verify',
          component: () => import('./components/AdminVerify.vue'),
        },
      ],
    },
    {
      path: '/tenant',
      component: () => import(/* webpackChunkName: "tenant" */ './views/TenantDashboard.vue'),
      meta: { requiresAuth: true, accountType: 'tenant' },
      children: [
        {
          path: 'orders',
          name: 'tenant',
          component: () => import(/* webpackChunkName: "tenant" */ './components/TentantOrders.vue'),
        },
        {
          path: 'transactions',
          component: () => import(/* webpackChunkName: "tenant" */ './components/TenantTransactions.vue'),
        },
        {
          path: 'payment',
          component: () => import(/* webpackChunkName: "tenant" */ './components/TenantPayments.vue'),
        },
        {
          path: 'documents',
          component: () => import(/* webpackChunkName: "tenant" */ './components/TenantDocuments.vue'),
        },
        {
          path: 'add-order',
          component: () => import(/* webpackChunkName: "tenant" */ './components/AddWorkOrder.vue'),
        },
      ],
    },
    {
      path: '/owner',
      component: () => import(/* webpackChunkName: "owner" */ './views/OwnerDashboard.vue'),
      meta: { requiresAuth: true, accountType: 'owner' },
      children: [
        {
          path: 'accounting',
          component: () => import(/* webpackChunkName: "owner" */ './components/Accounting.vue'),
          name: 'owner',
        },
        {
          path: 'properties',
          component: () => import(/* webpackChunkName: "owner" */ './components/Properties.vue'),
        },
        {
          path: 'edit-property/:id',
          component: () => import(/* webpackChunkName: "owner" */ './components/EditProperty.vue'),
        },
        {
          path: 'contact',
          component: () => import(/* webpackChunkName: "owner" */ './components/OwnerContact.vue'),
        },
        {
          path: 'details',
          component: () => import(/* webpackChunkName: "owner" */ './components/OwnerDetails.vue'),
        },
      ],
    },
    {
      path: '/new-account',
      component: () => import('./views/CreateAdminFlow.vue'),
    },
    {
      path: '/',
      name: 'home',
      redirect: { name: 'redirect' },
      meta: { requiresAuth: true },
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!auth.currentUser) {
      return next({ name: 'login' });
    }
  }
  const { uid } = auth.currentUser || { uid: null };
  let accountType = null;
  if (uid) {
    accountType = (await db
      .collection('users')
      .doc(uid)
      .get()).get('accountType');
  }
  if (to.matched.some(record => record.meta.accountType
        && record.meta.accountType !== accountType)) {
    return next({ name: 'redirect' });
  }
  if (to.name === 'redirect') {
    // check credentials here
    if (!uid) return next({ name: 'login' });
    return next({ name: accountType });
  }
  return next();
});

export default router;
